import {
  agentDetailResponseSchema,
  agentListResponseSchema,
  agentSummarySchema,
  browserCommandResponseSchema,
  browserProfileSchema,
  pairingKeyListResponseSchema,
  pairingKeyResponseSchema,
  revokeAgentResponseSchema,
  SESSION_STATUSES_REQUIRING_USER,
  type AgentDetailResponse,
  type AgentStatusChanged,
  type AgentSummary,
  type BrowserCommandResponse,
  type BrowserControlAction,
  type BrowserProfile,
  type BrowserStatusChanged,
  type CreatePairingKeyRequest,
  type Platform,
  type PlatformSessionChanged,
  type PaginationMeta,
  type PairingKeyResponse,
  type PairingKeySummary,
  type ProjectStatus,
} from '@midiautolistening/contracts';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { ApiRequestError, apiGet, apiPatch, apiPost, toApiError } from '@/lib/api';

/**
 * Agents, their browser profiles and the browser control commands.
 *
 * The screen shows THREE independent signals and this store keeps them
 * independent, because they genuinely are: an ONLINE agent can hold a LOGGED_OUT
 * Facebook session, and a LOGGED_IN session can belong to a PAUSED project.
 * Collapsing them into one "health" flag would hide exactly the state the operator
 * needs to act on.
 *
 *   1. `AgentSummary.status`            — agent process online/offline
 *   2. `BrowserProfile.sessionStatus`   — Facebook logged in or not
 *   3. `BrowserProfile.assignedProjectId` + `status` — project running
 */

/** Profile detail keyed by agent id, filled from `GET /agents/:id`. */
type AgentDetailMap = Record<string, AgentDetailResponse>;

export const useAgentsStore = defineStore('agents', () => {
  const items = ref<AgentSummary[]>([]);
  const meta = ref<PaginationMeta | null>(null);
  const loading = ref(false);
  const error = ref<ApiRequestError | null>(null);

  const details = ref<AgentDetailMap>({});
  const detailErrors = ref<Record<string, string>>({});

  const pairingKeys = ref<PairingKeySummary[]>([]);
  const pairingKeysError = ref<ApiRequestError | null>(null);
  /**
   * The plaintext pairing key, held in memory only and only until the dialog is
   * dismissed. The server stores a hash, so it cannot be read back — which is why
   * the dialog says it will not be shown again, and why it is never persisted.
   */
  const issuedPairingKey = ref<PairingKeyResponse | null>(null);
  const issuingPairingKey = ref(false);

  /** `browserProfileId` -> control action currently in flight. */
  const controlInFlight = ref<Record<string, BrowserControlAction>>({});
  const lastCommand = ref<BrowserCommandResponse | null>(null);

  const allProfiles = computed<BrowserProfile[]>(() =>
    Object.values(details.value).flatMap((detail) => detail.browserProfiles),
  );

  const profilesNeedingUserAction = computed<BrowserProfile[]>(() =>
    allProfiles.value.filter(
      (profile) =>
        profile.status === 'USER_ACTION_REQUIRED' ||
        SESSION_STATUSES_REQUIRING_USER.includes(profile.sessionStatus),
    ),
  );

  async function fetchList(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const response = await apiGet('/agents', agentListResponseSchema, {
        page: 1,
        pageSize: 50,
        includeRevoked: false,
      });
      items.value = response.items;
      meta.value = response.meta;
      await Promise.all(response.items.map((agent) => fetchDetail(agent.id)));
    } catch (caught) {
      error.value = toApiError(caught);
      items.value = [];
      meta.value = null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * One agent's profiles. A failure here is recorded per agent rather than
   * thrown: one unreachable agent must not blank out the whole screen.
   */
  async function fetchDetail(agentId: string): Promise<void> {
    try {
      const response = await apiGet(`/agents/${agentId}`, agentDetailResponseSchema);
      details.value = { ...details.value, [agentId]: response };
      const next = { ...detailErrors.value };
      delete next[agentId];
      detailErrors.value = next;
    } catch (caught) {
      const apiError = toApiError(caught);
      detailErrors.value = { ...detailErrors.value, [agentId]: apiError.message };
    }
  }

  /**
   * Register a Chromium profile against an agent.
   *
   * The agent creates the directory itself on first launch; this only allocates
   * the row the control buttons act on. Without one there is nothing to sign into
   * Facebook with, so this is the first thing to do after pairing.
   */
  async function createProfile(
    agentId: string,
    name: string,
    platform: Platform = 'FACEBOOK',
  ): Promise<BrowserProfile> {
    const created = await apiPost('/browser-profiles', browserProfileSchema, {
      agentId,
      name,
      platform,
    });
    await fetchDetail(agentId);
    return created;
  }

  async function renameAgent(agentId: string, deviceName: string): Promise<void> {
    const updated = await apiPatch(`/agents/${agentId}`, agentSummarySchema, { deviceName });
    items.value = items.value.map((agent) => (agent.id === agentId ? updated : agent));
  }

  async function revokeAgent(agentId: string, reason?: string): Promise<string[]> {
    const response = await apiPost(`/agents/${agentId}/revoke`, revokeAgentResponseSchema, {
      reason,
    });
    items.value = items.value.filter((agent) => agent.id !== agentId);
    const nextDetails = { ...details.value };
    delete nextDetails[agentId];
    details.value = nextDetails;
    return response.stoppedProjectIds;
  }

  // -------------------------------------------------------------------------
  // Pairing
  // -------------------------------------------------------------------------

  async function fetchPairingKeys(): Promise<void> {
    pairingKeysError.value = null;
    try {
      const response = await apiGet('/agents/pairing-keys', pairingKeyListResponseSchema);
      pairingKeys.value = response.items;
    } catch (caught) {
      pairingKeysError.value = toApiError(caught);
      pairingKeys.value = [];
    }
  }

  async function issuePairingKey(payload: CreatePairingKeyRequest): Promise<PairingKeyResponse> {
    issuingPairingKey.value = true;
    try {
      const response = await apiPost('/agents/pairing-key', pairingKeyResponseSchema, payload);
      issuedPairingKey.value = response;
      await fetchPairingKeys();
      return response;
    } finally {
      issuingPairingKey.value = false;
    }
  }

  /** Wipe the plaintext key from memory as soon as the dialog closes. */
  function forgetIssuedPairingKey(): void {
    issuedPairingKey.value = null;
  }

  // -------------------------------------------------------------------------
  // Browser control — every action maps to one allowlisted agent command
  // -------------------------------------------------------------------------

  async function controlBrowser(
    browserProfileId: string,
    action: BrowserControlAction,
    body: Record<string, unknown> = {},
  ): Promise<BrowserCommandResponse> {
    controlInFlight.value = { ...controlInFlight.value, [browserProfileId]: action };
    try {
      const response = await apiPost(
        `/browser-profiles/${browserProfileId}/control`,
        browserCommandResponseSchema,
        { action, ...body },
      );
      lastCommand.value = response;
      return response;
    } finally {
      const next = { ...controlInFlight.value };
      delete next[browserProfileId];
      controlInFlight.value = next;
    }
  }

  // -------------------------------------------------------------------------
  // Realtime patching — the three signals move independently
  // -------------------------------------------------------------------------

  function applyAgentStatusChanged(event: AgentStatusChanged): void {
    items.value = items.value.map((agent) =>
      agent.id === event.agentId
        ? { ...agent, status: event.status, lastHeartbeatAt: event.lastHeartbeatAt }
        : agent,
    );
  }

  function patchProfile(
    agentId: string,
    browserProfileId: string,
    patch: Partial<BrowserProfile>,
  ): void {
    const detail = details.value[agentId];
    if (!detail) return;

    details.value = {
      ...details.value,
      [agentId]: {
        ...detail,
        browserProfiles: detail.browserProfiles.map((profile) =>
          profile.id === browserProfileId ? { ...profile, ...patch } : profile,
        ),
      },
    };
  }

  function applyBrowserStatusChanged(event: BrowserStatusChanged): void {
    patchProfile(event.agentId, event.browserProfileId, {
      status: event.status,
      interactiveOpen: event.interactiveOpen,
      statusMessage: event.detail,
      lastStatusAt: event.occurredAt,
    });
  }

  function applyPlatformSessionChanged(event: PlatformSessionChanged): void {
    patchProfile(event.agentId, event.browserProfileId, {
      sessionStatus: event.sessionStatus,
      displayName: event.displayName,
      avatarUrl: event.avatarUrl,
      /**
       * The session frame moves `lastSessionCheckAt`, NOT `lastStatusAt`.
       *
       * They are different clocks: one says when Facebook was last looked at,
       * the other when the browser last changed lifecycle state. Writing the
       * session time into the browser field made the card claim the browser had
       * just changed every three seconds, and left the field the session UI
       * actually reads permanently stale.
       */
      lastSessionCheckAt: event.occurredAt,
    });
  }

  /**
   * Keeps the third signal ("project đang chạy") in step with `project.status_changed`.
   * Only the agent that actually owns a profile bound to the project is touched.
   *
   * `assignedProjectStatus` moves with the same event: the Start dialog decides
   * whether a profile can be taken over from this field, so leaving it stale
   * would hide a profile whose project has just stopped until the next full fetch.
   */
  function applyProjectStatus(projectId: string, status: ProjectStatus): void {
    const isRunning = status === 'RUNNING';
    const next: AgentDetailMap = {};

    for (const [agentId, detail] of Object.entries(details.value)) {
      const ownsProject = detail.browserProfiles.some(
        (profile) => profile.assignedProjectId === projectId,
      );
      const activeProjectIds =
        isRunning && ownsProject
          ? Array.from(new Set([...detail.activeProjectIds, projectId]))
          : detail.activeProjectIds.filter((id) => id !== projectId);

      const browserProfiles = ownsProject
        ? detail.browserProfiles.map((profile) =>
            profile.assignedProjectId === projectId
              ? { ...profile, assignedProjectStatus: status }
              : profile,
          )
        : detail.browserProfiles;

      next[agentId] = { ...detail, activeProjectIds, browserProfiles };
    }

    details.value = next;
  }

  function reset(): void {
    items.value = [];
    meta.value = null;
    error.value = null;
    details.value = {};
    detailErrors.value = {};
    pairingKeys.value = [];
    pairingKeysError.value = null;
    issuedPairingKey.value = null;
    controlInFlight.value = {};
    lastCommand.value = null;
  }

  return {
    items,
    meta,
    loading,
    error,
    details,
    detailErrors,
    pairingKeys,
    pairingKeysError,
    issuedPairingKey,
    issuingPairingKey,
    controlInFlight,
    lastCommand,
    allProfiles,
    profilesNeedingUserAction,
    fetchList,
    fetchDetail,
    createProfile,
    renameAgent,
    revokeAgent,
    fetchPairingKeys,
    issuePairingKey,
    forgetIssuedPairingKey,
    controlBrowser,
    applyAgentStatusChanged,
    applyBrowserStatusChanged,
    applyPlatformSessionChanged,
    applyProjectStatus,
    reset,
  };
});
